import { useEffect, useState, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api from '../services/api';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import {
  User,
  Lock,
  Save,
  Briefcase,
  Heart,
  CreditCard,
  Camera,
  Trash2,
} from 'lucide-react';

const API_BASE = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) || '';

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = useRef(null);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    // Parent/Customer fields
    billing_address: '',
    preferred_payment_method: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    // Coach fields
    display_name: '',
    specialization: '',
    bio: '',
    address: '',
  });

  // WhatsApp opt-in (parent-only)
  const [waOptIn, setWaOptIn] = useState(true);
  const [waSaving, setWaSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/auth/me/profile');
      setProfile(res.data);
      
      // Handle billing_address which might be an object or string
      let billingAddr = res.data.billing_address || '';
      if (typeof billingAddr === 'object' && billingAddr !== null) {
        // Convert object to string representation
        const parts = [];
        if (billingAddr.street) parts.push(billingAddr.street);
        if (billingAddr.city) parts.push(billingAddr.city);
        if (billingAddr.state) parts.push(billingAddr.state);
        if (billingAddr.zip) parts.push(billingAddr.zip);
        billingAddr = parts.join(', ');
      }
      
      setFormData({
        name: res.data.name || '',
        phone: res.data.phone || '',
        billing_address: billingAddr,
        preferred_payment_method: res.data.preferred_payment_method || '',
        emergency_contact_name: res.data.emergency_contact_name || '',
        emergency_contact_phone: res.data.emergency_contact_phone || '',
        display_name: res.data.display_name || '',
        specialization: res.data.specialization || '',
        bio: res.data.bio || '',
        address: res.data.address || '',
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }

    // Load WhatsApp opt-in (parent/customer only)
    try {
      const res = await api.get('/whatsapp/opt-in');
      setWaOptIn(Boolean(res.data?.opt_in));
    } catch {
      // non-fatal; keep default
    }
  };

  const handleWaOptInToggle = async (value) => {
    setWaSaving(true);
    const prev = waOptIn;
    setWaOptIn(value);  // optimistic
    try {
      await api.put('/whatsapp/opt-in', { opt_in: value });
      toast.success(value ? 'WhatsApp updates enabled' : 'WhatsApp updates disabled');
    } catch (e) {
      setWaOptIn(prev);
      toast.error('Failed to update preference');
    } finally {
      setWaSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/auth/me/profile', formData);
      toast.success('Profile updated successfully');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    try {
      await api.put('/auth/me/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully');
      setShowPasswordDialog(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPicture(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/auth/me/profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile picture updated');
      setProfile(prev => ({ ...prev, profile_picture: response.data.profile_picture }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload picture');
    } finally {
      setUploadingPicture(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePicture = async () => {
    try {
      await api.delete('/auth/me/profile-picture');
      toast.success('Profile picture removed');
      setProfile(prev => ({ ...prev, profile_picture: null }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove picture');
    }
  };

  const getProfilePictureUrl = () => {
    if (!profile?.profile_picture) return null;
    // If it starts with http, return as is, otherwise prepend API base
    if (profile.profile_picture.startsWith('http')) return profile.profile_picture;
    return `${API_BASE}${profile.profile_picture}`;
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'coach': return 'bg-purple-100 text-purple-800';
      case 'staff': return 'bg-blue-100 text-blue-800';
      case 'parent': return 'bg-green-100 text-green-800';
      case 'customer': return 'bg-amber-100 text-amber-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Profile">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  const isParentOrCustomer = profile?.role === 'parent' || profile?.role === 'customer';
  const isCoach = profile?.role === 'coach';
  const isStaff = profile?.role === 'staff';

  return (
    <DashboardLayout title="My Profile">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
        {/* Header Card with Profile Picture */}
        <Card className="border-2 border-slate-200">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center gap-6">
              {/* Profile Picture Section */}
              <div className="relative group">
                <div className="h-24 w-24 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                  {getProfilePictureUrl() ? (
                    <img 
                      src={getProfilePictureUrl()} 
                      alt="Profile" 
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <User className="h-12 w-12 text-slate-400" />
                  )}
                </div>
                
                {/* Upload Overlay */}
                <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePictureUpload}
                    accept="image/*"
                    className="hidden"
                    data-testid="profile-picture-input"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPicture}
                  >
                    {uploadingPicture ? (
                      <span className="animate-spin">⏳</span>
                    ) : (
                      <Camera className="h-5 w-5" />
                    )}
                  </Button>
                </div>
                
                {/* Delete button if picture exists */}
                {profile?.profile_picture && (
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={handleDeletePicture}
                    title="Remove picture"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>

              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-900">{profile?.name}</h2>
                <p className="text-slate-600">{profile?.email}</p>
                <Badge className={`mt-1 ${getRoleBadgeColor(profile?.role)}`}>
                  {profile?.role?.toUpperCase()}
                </Badge>
                <p className="text-xs text-slate-400 mt-2">
                  Hover over picture to change • Max 5MB
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowPasswordDialog(true)}
                data-testid="change-password-btn"
              >
                <Lock className="h-4 w-4 mr-2" />
                Change Password
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your full name"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Your phone number"
                  data-testid="profile-phone-input"
                />
              </div>
            </div>
            <div>
              <Label>Email Address</Label>
              <Input value={profile?.email} disabled className="bg-slate-50" />
              <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
            </div>
          </CardContent>
        </Card>

        {/* Coach-specific fields */}
        {isCoach && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Coach Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Display Name</Label>
                  <Input
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    placeholder="e.g., Coach John"
                    data-testid="profile-display-name-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Used in schedules and assignments</p>
                </div>
                <div>
                  <Label>Specialization</Label>
                  <Input
                    value={formData.specialization}
                    onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                    placeholder="e.g., Batting, Bowling"
                  />
                </div>
              </div>
              <div>
                <Label>Bio</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Tell us about your coaching experience..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Address</Label>
                <Textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Your address"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Staff-specific fields */}
        {isStaff && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Staff Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Address</Label>
                <Textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Your address"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Parent/Customer-specific fields */}
        {isParentOrCustomer && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Billing Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Billing Address</Label>
                <Textarea
                  value={formData.billing_address}
                  onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                  placeholder="Your billing address"
                  rows={2}
                />
              </div>
              <div>
                <Label>Preferred Payment Method</Label>
                <Select
                  value={formData.preferred_payment_method}
                  onValueChange={(value) => setFormData({ ...formData, preferred_payment_method: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="venmo">Venmo</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="card">Credit/Debit Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* WhatsApp updates (Parent/Customer-only) */}
        {isParentOrCustomer && (
          <Card data-testid="whatsapp-optin-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <svg className="h-5 w-5 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
                WhatsApp Updates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <Label className="text-sm">Receive game reminders, billing updates & announcements via WhatsApp</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    We'll send messages to your phone number on file. Regular message rates may apply. You can turn this off anytime.
                  </p>
                </div>
                <Switch
                  checked={waOptIn}
                  onCheckedChange={handleWaOptInToggle}
                  disabled={waSaving}
                  data-testid="whatsapp-optin-switch"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Emergency Contact (for all except admin) */}
        {profile?.role !== 'admin' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5" />
                Emergency Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={formData.emergency_contact_name}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                    placeholder="Emergency contact name"
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                    placeholder="Emergency contact phone"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="save-profile-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {/* Change Password Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Current Password</Label>
                <Input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  placeholder="Enter new password"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label>Confirm New Password</Label>
                <Input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleChangePassword} data-testid="submit-password-btn">
                Change Password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
